import { useState, useEffect } from 'react';
import { Heart, ChevronLeft, X, ExternalLink, Users, MessageCircle } from 'lucide-react';
import { SubredditWidget } from './SubredditWidget';
import { cn } from '../lib/utils';
import { SupportKoFiButton } from './SupportKoFiButton';
import { SUBREDDIT as subredditData } from '../lib/subreddit';
import { useSubredditStats } from '../hooks/useSubredditStats';

interface ResponsiveSubredditWidgetProps {
  onShare?: () => void;
  className?: string;
}

export function ResponsiveSubredditWidget({
  onShare,
  className
}: ResponsiveSubredditWidgetProps) {
  const [isPopoutOpen, setIsPopoutOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const { members } = useSubredditStats();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Close popout when resizing to wide viewport
      if (window.innerWidth >= 1732) {
        setIsPopoutOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show full widget on wide screens (1732px and wider)
  const showFullWidget = windowWidth >= 1732;

  const handleTabClick = () => {
    setIsPopoutOpen(true);
  };

  const handleClosePopout = () => {
    setIsPopoutOpen(false);
  };

  const handleBackdropClick = () => {
    setIsPopoutOpen(false);
  };

  const handleVisitSubreddit = () => {
    window.open(subredditData.url, '_blank', 'noopener,noreferrer');
    setIsPopoutOpen(false); // Close popout when visiting subreddit
  };

  const handleShare = () => {
    if (onShare) {
      onShare();
    } else {
      // Default share behavior
      if (navigator.share) {
        navigator.share({
          title: `Check out ${subredditData.fullName}`,
          text: subredditData.description,
          url: subredditData.url,
        });
      } else {
        // Fallback to copying URL
        navigator.clipboard.writeText(subredditData.url);
      }
    }
  };

  if (showFullWidget) {
    return (
      <div className={className}>
        <SubredditWidget
          data-testid="subreddit-full-widget"
          onShare={handleShare}
        />
        <SupportKoFiButton />
      </div>
    );
  }

  return (
    <>
      {/* Tab Button - responsive positioning based on screen size */}
      <button
        data-testid="subreddit-tab-button"
        onClick={handleTabClick}
        className={cn(
          // Base styles - solid background on mobile, transparent on desktop
          "fixed z-50",
          "border shadow-lg",
          "transition-all duration-300",

          // Mobile styles (default) - solid opaque background for visibility
          "bg-orange-600 border-orange-500 text-white",
          "hover:bg-orange-500 active:bg-orange-700",

          // Desktop/Tablet styles (768px and up) - transparent with blur
          "sm:bg-gradient-to-br sm:from-orange-900/20 sm:to-red-900/10",
          "sm:border-orange-500/30 sm:backdrop-blur-sm",
          "sm:hover:shadow-xl sm:hover:shadow-orange-500/10 sm:hover:border-orange-400/50",
          "sm:text-orange-400 sm:hover:text-orange-300",

          // Desktop/Tablet positioning (768px and up) - right edge
          "sm:flex sm:top-1/2 sm:right-0 sm:-translate-y-1/2",
          "sm:border-r-0 sm:rounded-md sm:min-w-[38px] sm:h-20 sm:px-2 sm:py-3",
          "sm:flex-col sm:items-center sm:justify-center sm:gap-1 sm:rounded-l-lg",

          // Mobile positioning (below 768px) - bottom right floating button (above latest confirmed)
          "flex bottom-20 right-4 w-11 h-11 rounded-full sm:w-auto sm:h-auto sm:bottom-auto",
          "items-center justify-center shadow-2xl sm:shadow-lg"
        )}
      >
        {/* Desktop layout */}
        <ChevronLeft
          data-testid="chevron-left"
          className="h-3 w-3 flex-shrink-0 sm:block hidden"
        />
        <Heart className="h-4 w-4 flex-shrink-0 sm:block hidden" />
        <span className="text-xs font-medium whitespace-nowrap sm:block hidden">PARTNER</span>

        {/* Mobile layout - just heart icon */}
        <Heart className="h-5 w-5 sm:hidden block" />
      </button>

      {/* Ko-fi support, stacked under the subreddit tab once the box collapses */}
      <SupportKoFiButton variant="tab" />

      {/* Backdrop */}
      {isPopoutOpen && (
        <div
          data-testid="subreddit-backdrop"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          onClick={handleBackdropClick}
        />
      )}

      {/* Compact Side Panel - responsive positioning */}
      {isPopoutOpen && (
        <div
          data-testid="subreddit-side-panel"
          className={cn(
            "fixed z-50 bg-gray-900/95 backdrop-blur-sm",
            "border border-orange-500/30 rounded-lg shadow-2xl overflow-hidden",

            // Desktop: next to tab button (right edge)
            "sm:top-1/2 sm:-translate-y-1/2 sm:right-16 sm:w-80",

            // Mobile: centered modal with proper spacing
            "left-4 right-4 bottom-auto top-1/2 -translate-y-1/2 w-auto max-w-sm mx-auto",
            "sm:left-auto sm:right-16 sm:translate-y-[-50%] sm:max-w-none sm:mx-0"
          )}
        >
          {/* Close button */}
          <button
            data-testid="subreddit-close-button"
            onClick={handleClosePopout}
            className={cn(
              "absolute top-2 right-2 z-10",
              "w-6 h-6 rounded-full",
              "bg-gray-800/80 hover:bg-gray-700/80",
              "border border-gray-600/50 hover:border-gray-500/50",
              "flex items-center justify-center",
              "text-gray-400 hover:text-white",
              "transition-all duration-200"
            )}
          >
            <X className="h-3 w-3" />
          </button>

          {/* Compact widget content */}
          <div className="p-3">
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <Heart className="h-4 w-4 text-orange-400" />
              <h3 className="text-sm font-semibold text-orange-400">Community Partner</h3>
              <span className="bg-orange-500/20 text-orange-400 text-xs px-2 py-0.5 rounded-full border border-orange-500/30">
                Collab
              </span>
            </div>

            {/* Subreddit Info */}
            <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/30 p-4 mb-3">
              {/* Reddit-style background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-1 left-1 w-2 h-2 bg-orange-400 rounded-full"></div>
                <div className="absolute top-3 right-2 w-1 h-1 bg-red-400 rounded-full"></div>
                <div className="absolute bottom-1 left-4 w-2 h-2 bg-orange-300 rounded-full"></div>
                <div className="absolute bottom-2 right-1 w-1 h-1 bg-red-300 rounded-full"></div>
              </div>
              
              {/* Subreddit content */}
              <div className="relative text-center space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <MessageCircle className="h-5 w-5 text-orange-400" />
                  <h4 className="text-lg font-bold text-white">{subredditData.fullName}</h4>
                </div>
                <p className="text-gray-300 text-xs leading-relaxed">
                  {subredditData.description}
                </p>
                <div className="flex items-center justify-center gap-2 text-xs">
                  <div className="flex items-center gap-1 text-orange-300">
                    <Users className="h-3 w-3" />
                    <span>{members} members</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Partnership info */}
            <div className="space-y-2 mb-3">
              <div className="text-xs text-gray-400">
                <span className="text-orange-400 font-medium">Partnership:</span>{" "}
                Community Collaboration
              </div>
              <div className="text-xs text-gray-400">
                <span className="text-orange-400 font-medium">Focus:</span>{" "}
                Distant views & perspectives
              </div>
              <div className="text-xs text-gray-400">
                <span className="text-orange-400 font-medium">Status:</span>{" "}
                Active & Growing
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={handleVisitSubreddit}
                className={cn(
                  "flex-1 text-xs px-3 py-1.5 rounded-md",
                  "bg-orange-600/20 hover:bg-orange-600/30",
                  "border border-orange-500/30 hover:border-orange-400/50",
                  "text-orange-400 hover:text-orange-300",
                  "transition-all duration-200"
                )}
              >
                Visit r/FromAfar
              </button>
              <button
                data-testid="share-button"
                onClick={handleShare}
                className={cn(
                  "px-2 py-1.5 rounded-md",
                  "bg-gray-700/50 hover:bg-gray-600/50",
                  "border border-gray-600/50 hover:border-gray-500/50",
                  "text-gray-400 hover:text-gray-300",
                  "transition-all duration-200"
                )}
              >
                <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
} 